/**
 * Schema (zod) do JSON de config — o contrato que backend, painel e SDK compartilham.
 *
 * Duas decisões que valem explicação:
 *
 * 1. Todo objeto é `.strict()`. Campo desconhecido derruba a validação em vez de passar batido.
 *    É o que impede um "segredo inline" (`{ token: "sk-..." }` grudado num bloco) de vazar para
 *    dentro da config pública por acidente.
 *
 * 2. Cores e URLs são validadas com regex apertada. Elas terminam dentro de uma string de CSS
 *    e de atributos no Shadow DOM do SDK — deixar `;` ou `url(...)` passar seria injeção de CSS.
 */
import { z } from 'zod';
import { isValidDomainEntry } from './domain.js';
const MAX_TIMEOUT_MS = 30000;
const DEFAULT_TIMEOUT_MS = 10000;
/** `#rgb`, `#rrggbb(aa)`, `rgb()/rgba()` ou uma palavra-chave CSS. Nada de `;`, `}` ou `url(`. */
const cssColor = z
    .string()
    .trim()
    .max(64)
    .refine((v) => /^#[0-9a-fA-F]{3,8}$/.test(v) ||
    /^rgba?\(\s*[\d.\s,%/]+\)$/.test(v) ||
    /^[a-zA-Z]{3,24}$/.test(v), { message: 'Cor inválida: use hex (#25D366), rgb()/rgba() ou uma palavra-chave CSS' });
const httpUrl = z
    .string()
    .trim()
    .max(2048)
    .refine((v) => {
    try {
        const u = new URL(v);
        return u.protocol === 'https:' || u.protocol === 'http:';
    }
    catch {
        return false;
    }
}, { message: 'URL deve ser http(s) absoluta' });
/** URL de webhook: aceita `{{variavel}}` no meio, então valida só depois de trocar por placeholder. */
const templateUrl = z
    .string()
    .trim()
    .min(1)
    .max(2048)
    .refine((v) => {
    const probe = v.replace(/\{\{[^}]*\}\}/g, 'x');
    try {
        const u = new URL(probe);
        return u.protocol === 'https:' || u.protocol === 'http:';
    }
    catch {
        return false;
    }
}, { message: 'URL deve ser http(s) absoluta (pode conter {{variaveis}})' });
/** Nome de variável: é o que a interpolação `{{nome}}` procura. */
export const variableName = z
    .string()
    .trim()
    .min(1)
    .max(64)
    .regex(/^[A-Za-z_][A-Za-z0-9_]*$/, 'Use letras, números e _ (começando por letra ou _)');
const timeoutMs = z.number().int().min(100).max(MAX_TIMEOUT_MS).optional();
const headerPairSchema = z
    .object({
    key: z
        .string()
        .trim()
        .min(1)
        .max(128)
        .regex(/^[A-Za-z0-9!#$%&'*+\-.^_`|~]+$/, 'Nome de header inválido'),
    value: z.string().max(4096),
})
    .strict();
const httpMethodSchema = z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']);
const contentTypeSchema = z.enum([
    'application/json',
    'application/x-www-form-urlencoded',
    'text/plain',
]);
// ---------------------------------------------------------------------------
// Blocos
// ---------------------------------------------------------------------------
const labelField = z.string().trim().max(80).optional();
export const messageDataSchema = z
    .object({ label: labelField, text: z.string().min(1).max(5000) })
    .strict();
export const inputDataSchema = z
    .object({
    label: labelField,
    prompt: z.string().min(1).max(5000),
    saveAs: variableName,
    inputType: z.enum(['text', 'email', 'phone', 'number']),
    placeholder: z.string().max(120).optional(),
})
    .strict();
export const buttonOptionSchema = z
    .object({
    id: z.string().trim().min(1).max(64),
    label: z.string().trim().min(1).max(120),
    value: z.string().max(500).optional(),
})
    .strict();
export const buttonsDataSchema = z
    .object({
    label: labelField,
    prompt: z.string().min(1).max(5000),
    options: z.array(buttonOptionSchema).min(1).max(12),
    saveAs: variableName.optional(),
})
    .strict()
    .refine((d) => new Set(d.options.map((o) => o.id)).size === d.options.length, {
    message: 'Os ids das opções precisam ser únicos (são os handles das arestas)',
    path: ['options'],
});
export const conditionRuleSchema = z
    .object({
    variable: variableName,
    operator: z.enum([
        'eq',
        'neq',
        'gt',
        'gte',
        'lt',
        'lte',
        'contains',
        'notContains',
        'isEmpty',
        'isNotEmpty',
    ]),
    value: z.string().max(500).optional(),
})
    .strict();
export const conditionDataSchema = z
    .object({
    label: labelField,
    mode: z.enum(['builder', 'expression']),
    combinator: z.enum(['and', 'or']).optional(),
    rules: z.array(conditionRuleSchema).max(20).optional(),
    expression: z.string().max(1000).optional(),
})
    .strict()
    .refine((d) => (d.mode === 'builder' ? (d.rules?.length ?? 0) > 0 : !!d.expression?.trim()), {
    message: 'Condição em modo builder precisa de regras; em modo expressão, de uma expressão',
});
export const browserCodeDataSchema = z
    .object({
    label: labelField,
    code: z.string().max(20000),
    executionMode: z.enum(['page', 'sandbox']),
    saveAs: variableName.optional(),
    timeoutMs,
})
    .strict();
export const browserWebhookDataSchema = z
    .object({
    label: labelField,
    url: templateUrl,
    method: httpMethodSchema,
    headers: z.array(headerPairSchema).max(30),
    contentType: contentTypeSchema,
    body: z.string().max(20000).optional(),
    saveAs: variableName.optional(),
    timeoutMs,
})
    .strict();
/**
 * Webhook de backend. `url`/`headers`/`body`/`secretRefs` são opcionais porque a config
 * **pública** chega ao browser sem eles (`sanitizeConfigForPublic`). Na publicação,
 * `collectPublishBlockers` cobra a URL.
 */
export const backendWebhookDataSchema = z
    .object({
    label: labelField,
    url: templateUrl.optional(),
    method: httpMethodSchema.optional(),
    headers: z.array(headerPairSchema).max(30).optional(),
    contentType: contentTypeSchema.optional(),
    body: z.string().max(20000).optional(),
    saveAs: variableName.optional(),
    secretRefs: z.array(z.string().trim().min(1).max(128)).max(20).optional(),
    timeoutMs,
})
    .strict();
const nodeBase = { id: z.string().trim().min(1).max(64), position: z.object({ x: z.number(), y: z.number() }).strict() };
export const flowNodeSchema = z.discriminatedUnion('type', [
    z.object({ ...nodeBase, type: z.literal('message'), data: messageDataSchema }).strict(),
    z.object({ ...nodeBase, type: z.literal('input'), data: inputDataSchema }).strict(),
    z.object({ ...nodeBase, type: z.literal('buttons'), data: buttonsDataSchema }).strict(),
    z.object({ ...nodeBase, type: z.literal('condition'), data: conditionDataSchema }).strict(),
    z.object({ ...nodeBase, type: z.literal('browserCode'), data: browserCodeDataSchema }).strict(),
    z.object({ ...nodeBase, type: z.literal('browserWebhook'), data: browserWebhookDataSchema }).strict(),
    z.object({ ...nodeBase, type: z.literal('backendWebhook'), data: backendWebhookDataSchema }).strict(),
]);
export const flowEdgeSchema = z
    .object({
    id: z.string().trim().min(1).max(128),
    source: z.string().trim().min(1).max(64),
    target: z.string().trim().min(1).max(64),
    sourceHandle: z.string().max(64).nullish(),
    targetHandle: z.string().max(64).nullish(),
})
    .strict();
export const flowSchema = z
    .object({
    nodes: z.array(flowNodeSchema).max(500),
    edges: z.array(flowEdgeSchema).max(2000),
    startNodeId: z.string().trim().min(1).max(64),
})
    .strict()
    .superRefine((flow, ctx) => {
    const ids = new Set();
    for (const node of flow.nodes) {
        if (ids.has(node.id)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Nó duplicado: ${node.id}`, path: ['nodes'] });
        }
        ids.add(node.id);
    }
    if (flow.nodes.length > 0 && !ids.has(flow.startNodeId)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `startNodeId "${flow.startNodeId}" não existe entre os nós`,
            path: ['startNodeId'],
        });
    }
    for (const edge of flow.edges) {
        if (!ids.has(edge.source) || !ids.has(edge.target)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Aresta ${edge.id} aponta para um nó inexistente`,
                path: ['edges'],
            });
        }
    }
});
export const appearanceSchema = z
    .object({
    title: z.string().trim().min(1).max(80),
    subtitle: z.string().trim().max(120).optional(),
    imageUrl: httpUrl.optional(),
    position: z.enum(['bottom-right', 'bottom-left', 'top-right', 'top-left']),
    offsetX: z.number().int().min(0).max(400),
    offsetY: z.number().int().min(0).max(400),
    primaryColor: cssColor,
    bubbleColor: cssColor,
    textColor: cssColor,
    typingDelayMs: z.number().int().min(0).max(5000),
    autoOpenDelayMs: z.number().int().min(0).max(120000).optional(),
    /*
     * `optional()` e não `default()`: é a **ausência** do campo que faz o publicado antigo herdar o
     * tema padrão na próxima carga do widget. Com `default()`, o valor seria materializado na primeira
     * validação e o webchat ficaria preso ao padrão vigente naquele dia.
     */
    chatBackground: z.enum(['whatsapp-dark', 'whatsapp-light', 'plain']).optional(),
})
    .strict();
export const authorizedDomainsSchema = z
    .array(z.string().trim().min(1).max(253))
    .min(1, 'Informe ao menos um domínio autorizado')
    .max(50)
    .superRefine((domains, ctx) => {
    domains.forEach((d, i) => {
        if (!isValidDomainEntry(d)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Domínio inválido: "${d}". Use cliente.com, *.cliente.com ou localhost — "*" solto não é aceito.`,
                path: [i],
            });
        }
    });
});
export const webchatVersionSchema = z
    .object({ appearance: appearanceSchema, flow: flowSchema })
    .strict();
export const webchatConfigSchema = z
    .object({
    publicKey: z.string().trim().min(8).max(128),
    name: z.string().trim().max(120).optional(),
    authorizedDomains: authorizedDomainsSchema,
    appearance: appearanceSchema,
    flow: flowSchema,
    version: z.number().int().min(0),
    collectLeads: z.boolean().optional(),
})
    .strict();
function formatIssues(error) {
    return error.issues.map((i) => {
        const path = i.path.join('.');
        return path ? `${path}: ${i.message}` : i.message;
    });
}
export function validateConfig(input) {
    const parsed = webchatConfigSchema.safeParse(input);
    return parsed.success
        ? { ok: true, data: parsed.data }
        : { ok: false, errors: formatIssues(parsed.error) };
}
export function validateVersion(input) {
    const parsed = webchatVersionSchema.safeParse(input);
    return parsed.success
        ? { ok: true, data: parsed.data }
        : { ok: false, errors: formatIssues(parsed.error) };
}
export function validateFlow(input) {
    const parsed = flowSchema.safeParse(input);
    return parsed.success
        ? { ok: true, data: parsed.data }
        : { ok: false, errors: formatIssues(parsed.error) };
}
/**
 * Problemas que impedem publicar. Serve tanto para a Function `publishWebchat` recusar
 * quanto para o painel listar o que falta antes de o usuário clicar em Publicar.
 */
export function collectPublishBlockers(version, authorizedDomains) {
    const blockers = [];
    const versionCheck = validateVersion(version);
    if (!versionCheck.ok)
        blockers.push(...versionCheck.errors);
    const domainCheck = authorizedDomainsSchema.safeParse([...authorizedDomains]);
    if (!domainCheck.success)
        blockers.push(...formatIssues(domainCheck.error));
    const flow = version.flow;
    if (!flow?.nodes?.length) {
        blockers.push('O fluxo está vazio: adicione ao menos um bloco.');
        return blockers;
    }
    for (const node of flow.nodes) {
        if (node.type === 'backendWebhook' && !node.data.url?.trim()) {
            blockers.push(`Bloco "${node.data.label ?? node.id}": webhook de backend sem URL.`);
        }
        if (node.type === 'buttons') {
            const handles = new Set(flow.edges.filter((e) => e.source === node.id).map((e) => e.sourceHandle ?? ''));
            const orphans = node.data.options.filter((o) => !handles.has(o.id));
            if (orphans.length === node.data.options.length) {
                blockers.push(`Bloco "${node.data.label ?? node.id}": nenhum botão está conectado.`);
            }
        }
    }
    // Nós inalcançáveis não bloqueiam a publicação, mas o fluxo sem início bloqueia.
    if (!flow.nodes.some((n) => n.id === flow.startNodeId)) {
        blockers.push('O fluxo não tem bloco inicial definido.');
    }
    return blockers;
}
/**
 * Remove do webhook de backend tudo que o browser não precisa saber.
 *
 * O visitante só precisa do `nodeId` para pedir a execução à Function; URL, método, headers,
 * body e `secretRefs` ficam exclusivamente no documento server-side. Sem isso, publicar um
 * webchat exporia os endpoints internos do cliente para qualquer visitante do site.
 */
export function sanitizeConfigForPublic(config) {
    return {
        ...config,
        flow: {
            ...config.flow,
            nodes: config.flow.nodes.map((node) => {
                if (node.type !== 'backendWebhook')
                    return node;
                const { label, saveAs } = node.data;
                return {
                    ...node,
                    data: {
                        ...(label !== undefined ? { label } : {}),
                        ...(saveAs !== undefined ? { saveAs } : {}),
                    },
                };
            }),
        },
    };
}
export { DEFAULT_TIMEOUT_MS, MAX_TIMEOUT_MS };
//# sourceMappingURL=schema.js.map